import { encode, decode } from "base-64";

/**
 * Reversible, canonical, non-sequential obfuscation for numeric record IDs in URLs.
 *
 * This is FE-only obfuscation (NOT security): it hides the raw primary key and makes
 * adjacent IDs look unrelated so they can't be guessed/enumerated by eye. Unlike base64,
 * it is canonical (one token per ID) and breaks the sequential pattern (2293 vs 2294 produce
 * completely different tokens).
 *
 * Implementation: a multiplicative permutation over 2^32 (an invertible LCG step), then base36.
 * Added checksum validation to detect tampering.
 */
const ID_MOD = BigInt(4294967296); // 2^32
const ID_MULT = BigInt(2654435761); // Knuth's multiplicative constant (odd => invertible mod 2^32)
const ID_OFFSET = BigInt(1442695041);
const CHECKSUM_MULT = BigInt(3141592653); // Pi-based constant for checksum
const ZERO = BigInt(0);

const modInverse = (a: bigint, m: bigint): bigint => {
  let [oldR, r] = [((a % m) + m) % m, m];
  let [oldS, s] = [BigInt(1), ZERO];
  while (r !== ZERO) {
    const q = oldR / r;
    [oldR, r] = [r, oldR - q * r];
    [oldS, s] = [s, oldS - q * s];
  }
  return ((oldS % m) + m) % m;
};

const ID_MULT_INV = modInverse(ID_MULT, ID_MOD);

/**
 * Generate a checksum for the given ID to detect tampering
 */
const generateChecksum = (id: number): string => {
  const n = BigInt(id);
  const check = ((n * CHECKSUM_MULT) % BigInt(65536)); // 16-bit checksum
  return check.toString(36).padStart(3, '0'); // Always 3 characters in base36
};

/**
 * Verify that the checksum matches the decoded ID
 */
const verifyChecksum = (id: number, checksum: string): boolean => {
  return generateChecksum(id) === checksum;
};

export const encodeId = (id: number | string): string => {
  const num = typeof id === "string" ? parseInt(id, 10) : id;
  if (num === null || num === undefined || isNaN(num as number)) return "";
  const n = BigInt(num);
  const scrambled = (n * ID_MULT + ID_OFFSET) % ID_MOD;
  const encoded = scrambled.toString(36);
  const checksum = generateChecksum(num);
  return `${encoded}-${checksum}`; // Format: scrambledId-checksum
};

export const decodeId = (token?: string | null): number | null => {
  if (!token) return null;
  
  // Split token into encoded part and checksum
  const parts = token.split('-');
  if (parts.length !== 2) {
    // Invalid format - token was tampered with or is old format
    return null;
  }
  
  const [encoded, checksum] = parts;
  const scrambled = parseInt(encoded, 36);
  if (isNaN(scrambled)) return null;
  
  const n = BigInt(scrambled);
  const id = ((((n - ID_OFFSET) % ID_MOD) + ID_MOD) % ID_MOD * ID_MULT_INV) % ID_MOD;
  const decodedId = Number(id);
  
  // Verify checksum to detect tampering
  if (!verifyChecksum(decodedId, checksum)) {
    // Checksum mismatch - token was tampered with
    return null;
  }
  
  return decodedId;
};

export const base64Encode = (text: string) => {
  return encode(text)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const base64Decode = (text: string) => {
  if (!text) return "";

  // Fix URL encoding issues
  let base64 = decodeURIComponent(text)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  // Correct way to restore padding
  const pad = base64.length % 4;
  if (pad === 2) base64 += "==";
  else if (pad === 3) base64 += "=";
  else if (pad !== 0) throw new Error("Invalid Base64 input");

  return decode(base64);
};

// let base64 = require('base-64');
// let utf8 = require('utf8');

// export const base64Encode = (text: string) => {
//     return utf8.encode(base64.encode(text));
// };

// export const base64Decode = (text: string) => {
//     if (!text) return "";

//     return utf8.decode(base64.decode(text));
// };